import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, FormControl } from "react-bootstrap";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import {
  removeOtpRetryTimeoutData,
  setOtpRetryTimeoutData,
} from "../../../../utils";

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const VerifcationCodeLength = new Array(5).fill(0);

const VerificationInput = ({
  usedInSignup,
  usedIn,
  codeItem,
  isRequestingVerifyToken,
  disableResend,
  setCodeItem,
  onVerifyAccount,
  onResendCode,
  setIsChildLoading,
}) => {
  const inputRef = useRef();
  const [isFocused, setIsFocused] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const code = useMemo(() => codeItem.split(""), [codeItem]);

  const { usedInKeyForTimeStamp, usedInKeyForWaitSeconds } = useMemo(
    () => ({
      usedInKeyForTimeStamp: `${usedIn}_nextEligibleTimestamp`,
      usedInKeyForWaitSeconds: `${usedIn}_initialWaitSeconds`,
    }),
    [usedIn]
  );

  const selectedCodeItem = useMemo(
    () =>
      code.length < VerifcationCodeLength.length
        ? code.length
        : VerifcationCodeLength.length - 1,
    [code]
  );

  const onClickCode = useCallback(() => inputRef.current.focus(), []);
  const onCodeFocus = useCallback(() => setIsFocused(true), [setIsFocused]);
  const onCodeBlur = useCallback(() => setIsFocused(false), [setIsFocused]);

  const onCodeKeyUp = useCallback(
    (e) =>
      (e.key === "Backspace" || e.key === "Delete") &&
      setCodeItem(codeItem.slice(0, codeItem.length - 1)),
    [codeItem, setCodeItem]
  );

  const onChangeCodeItem = useCallback(
    (e) => {
      const codeItemValue = e.target.value;
      if (codeItem.length >= VerifcationCodeLength.length) {
        return null;
      } else {
        setCodeItem(
          (codeItem + codeItemValue).slice(0, VerifcationCodeLength.length)
        );
      }
    },
    [codeItem, setCodeItem]
  );

  const handleResendCode = useCallback(async () => {
    try {
      setIsResending(true);
      setIsChildLoading(true);
      const { nextEligibleTimestamp, waitSeconds } = await onResendCode();
      if (nextEligibleTimestamp && waitSeconds) {
        const dataToSave = [
          {
            key: usedInKeyForTimeStamp,
            value: nextEligibleTimestamp?.toString(),
          },
          {
            key: usedInKeyForWaitSeconds,
            value: waitSeconds?.toString(),
          },
        ];
        setOtpRetryTimeoutData(dataToSave);
        setCountdown(parseInt(waitSeconds, 10));
      }
    } catch (error) {
      console.error("Error resending code:", error);
      toast.error(
        <div>
          Failed to resend verification code!
          <br />
          {error.error || error.message
            ? `Error: ${error.error || error.message}`
            : "Please try again later."}
          <br />
          <small>If the issue persists, please contact P&S.</small>
        </div>
      );
    } finally {
      setIsResending(false);
      setIsChildLoading(false);
    }
  }, [
    onResendCode,
    usedInKeyForTimeStamp,
    usedInKeyForWaitSeconds,
    setIsChildLoading,
  ]);

  useEffect(() => {
    const checkExistingCountdown = () => {
      const storedNextEligibleTime = localStorage.getItem(
        usedInKeyForTimeStamp
      );
      const initialWaitSeconds = localStorage.getItem(usedInKeyForWaitSeconds);

      if (storedNextEligibleTime) {
        const nextEligibleTime = new Date(storedNextEligibleTime).getTime();
        const currentTime = new Date().getTime();
        const remainingSeconds = Math.max(
          0,
          Math.ceil((nextEligibleTime - currentTime) / 1000)
        );

        if (remainingSeconds > 0) {
          setCountdown(remainingSeconds);
        } else {
          removeOtpRetryTimeoutData([
            usedInKeyForTimeStamp,
            usedInKeyForWaitSeconds,
          ]);
        }
      } else if (initialWaitSeconds) {
        const waitSeconds = parseInt(initialWaitSeconds, 10);

        if (waitSeconds > 0) {
          setCountdown(waitSeconds);
          const nextEligibleTime = new Date(
            Date.now() + waitSeconds * 1000
          ).toISOString();

          setOtpRetryTimeoutData([
            {
              key: usedInKeyForTimeStamp,
              value: nextEligibleTime,
            },
          ]);
        }
        removeOtpRetryTimeoutData([usedInKeyForWaitSeconds]);
      }
    };

    checkExistingCountdown();
  }, [usedInKeyForTimeStamp, usedInKeyForWaitSeconds]);

  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            removeOtpRetryTimeoutData([
              usedInKeyForTimeStamp,
              usedInKeyForWaitSeconds,
            ]);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [countdown, usedInKeyForTimeStamp, usedInKeyForWaitSeconds]);

  const isResendDisabled = useMemo(
    () =>
      countdown > 0 || isRequestingVerifyToken || disableResend || isResending,
    [countdown, disableResend, isRequestingVerifyToken, isResending]
  );

  return (
    <>
      <div className="mt-5" onClick={onClickCode}>
        <div className="verification wrap-verification-code px-1">
          <FormControl
            ref={inputRef}
            type="number"
            value=""
            disabled={isRequestingVerifyToken}
            onFocus={onCodeFocus}
            onBlur={onCodeBlur}
            onKeyUp={onCodeKeyUp}
            onChange={onChangeCodeItem}
            className="verification hidden-input shadow-none"
            style={{
              width: "2.5rem",
              top: "0rem",
              bottom: "0rem",
              left: `${selectedCodeItem * 3.6}rem`,
              opacity: 0,
            }}
          />
          {VerifcationCodeLength.map((c, index) => {
            const selectedInputBox = code.length === index;
            const filled =
              code.length === VerifcationCodeLength.length &&
              index === VerifcationCodeLength.length - 1;

            return (
              <div className="verification individual-code" key={`c-${index}`}>
                {code[index]}
                {(selectedInputBox || filled) && isFocused && (
                  <div className="verification individual-code-shadows" />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <Button
            className="mx-3 px-5 py-1 rounded"
            variant={usedInSignup ? "pns" : "primary"}
            disabled={code.length < 5 || isRequestingVerifyToken}
            onClick={onVerifyAccount}
          >
            Verify
          </Button>
        </div>
      </div>
      <div className="mt-5">
        <div>
          <small>Didn't receive a code?</small>
        </div>
        {countdown > 0 ? (
          <div className="countdown-timer mt-2 text-center text-muted">
            Resend enabled in {formatTime(countdown)}
          </div>
        ) : (
          <Button
            variant="link"
            size="sm"
            className="resend-button"
            disabled={isResendDisabled}
            onClick={handleResendCode}
          >
            {isResending ? "Resending... " : "Resend "}
          </Button>
        )}
      </div>
    </>
  );
};

VerificationInput.defaultProps = {
  usedInSignup: false,
  usedIn: "",
  verifyMethod: "",
  codeItem: "",
  isRequestingVerifyToken: false,
  disableResend: false,
  setCodeItem: () => {},
  onVerifyAccount: () => {},
  onResendCode: () => {},
  setIsChildLoading: () => {},
};

VerificationInput.propTypes = {
  usedInSignup: PropTypes.bool,
  usedIn: PropTypes.string,
  verifyMethod: PropTypes.string,
  codeItem: PropTypes.string,
  isRequestingVerifyToken: PropTypes.bool,
  disableResend: PropTypes.bool,
  setCodeItem: PropTypes.func,
  onVerifyAccount: PropTypes.func,
  onResendCode: PropTypes.func,
  setIsChildLoading: PropTypes.func,
};

export default VerificationInput;
